import { store } from 'store';
import * as Yup from 'yup';

import dayjs from '~libs/dayjs';

import { TooltipIcon } from '~components';
import { faInfoCircle } from '~components/icon';
import { Column, Row } from '~components/table-view-v3';
import { selectEditor } from '~components/table-view-v3/columnEditors';
import { genericCellRender } from '~components/table-view-v3/columnRenderers';

import { AccountInvoiceCode } from '~support-at-home/actions/accountCodes';
import { getBudgetExternalServices } from '~support-at-home/actions/budgetServices';
import { sahServiceTypeAssociationsApi } from '~support-at-home/actions/sahServiceTypeAssociations';

import type { ServiceTypeRow } from '~appointments/reducers/serviceTypes';
import type { ClientPackageScheduleRow } from '~main/reducers/clients/clientPackageSchedules';

import ServiceDetail from '../../components/suppliers/supplier-invoices/ServiceDetail';
import BudgetIcon from './BudgetIcon';
import RefreshActionButton from './RefreshActionButton';

export const supplierInvoiceColumnsFactory = (
  mode: 'package' | 'po',
  accountInvoiceCodes: AccountInvoiceCode[],
  packageSchedules: ClientPackageScheduleRow[],
  serviceTypes: ServiceTypeRow[],
  clientId: number | undefined,
  setFormData: (updater: (prev: any) => any) => void,
  invoiceDate?: string,
): Column[] => {
  const packageOptions = packageSchedules.map(({ package: { id, name } }) => ({ id, name }));

  const getPackageOptionsForDate = (serviceDate?: string) => {
    const filterDate = serviceDate || invoiceDate;
    if (!filterDate || !dayjs(filterDate).isValid()) return packageOptions;

    const date = dayjs(filterDate);
    const filtered = packageSchedules
      .filter(
        ({ start_date, end_date }) =>
          date.isSameOrAfter(dayjs(start_date), 'day') && (!end_date || date.isSameOrBefore(dayjs(end_date), 'day')),
      )
      .map(({ package: { id, name } }) => ({ id, name }));

    return filtered;
  };

  // Shared columns (start)
  const startColumns: Column[] = [
    {
      id: 'reference',
      header: 'Ref.',
      type: 'custom',
      component: TooltipIcon,
      componentProps: (row: Row) => ({
        id: row.id,
        icon: faInfoCircle,
        tooltip: row.reference,
      }),
    },
    {
      id: 'service_date',
      header: 'Service Date',
      type: 'date',
      style: { minWidth: '150px' },
      onChange: fetchValidServices,
      validation: Yup.string()
        .test('valid-date', 'Invalid date', (val) => !val || dayjs(val).isValid())
        .required('Service Date is required'),
    },
    {
      id: 'description',
      header: 'Description',
      type: 'textarea',
      style: { minWidth: '250px' },
    },
    {
      id: 'units',
      header: 'Qty.',
      type: 'number',
      classNames: {
        header: 'cell-align-right',
        cell: 'cell-align-right',
      },
      componentProps: {
        noDecimals: false,
      },
      style: {
        width: '70px',
      },
      validation: Yup.number().min(0.1, 'Quantity must be greater than 0').required('Quantity is required'),
    },
    {
      id: 'unit_amount',
      header: 'Rate',
      type: 'currency',
      classNames: {
        header: 'cell-align-right',
        cell: 'cell-align-right',
      },
      style: { width: '100px' },
      validation: Yup.number().typeError('Rate must be a number').min(0, 'Rate cannot be negative').required(),
    },
    {
      id: 'gst_type',
      header: 'GST',
      type: 'select',
      classNames: {
        header: 'cell-align-left',
        column: 'cell-align-left',
      },
      options: [
        { id: 'free', name: 'GST Free' },
        { id: 'in', name: 'Included' },
        { id: 'nin', name: 'Not Included' },
      ],
      style: { minWidth: '140px' },
    },
    {
      id: 'total',
      header: 'Total',
      type: 'readonly',
      classNames: {
        header: 'cell-align-right',
        column: 'cell-align-right',
      },
      style: { minWidth: '100px' },
      isEditable: false,
      deriveValue: (row) => `$${deriveTotal(row).toFixed(2)}`,
    },
  ];

  // Shared columns (end)
  const endColumns: Column[] = [
    {
      id: 'budget_icon',
      header: 'Budget',
      type: 'custom',
      classNames: {
        header: 'cell-align-center',
        column: 'cell-align-center',
      },
      style: { width: '50px' },
      component: BudgetIcon,
      validation: Yup.mixed().test(
        'budget-found',
        'Budget not found for selected Service Type / PO #',
        (_value, context) => {
          return Boolean(context.parent?.client_budget_id);
        },
      ),
    },
    {
      id: 'shift_id',
      header: 'Shift',
      type: mode === 'package' ? 'select' : 'readonly',
      style: { minWidth: '150px' },
      renderEditor: selectEditor((row) => {
        if (mode === 'po') return [];

        const { validServices, service_type_id } = row._meta?.validServices
          ? { validServices: row._meta.validServices, service_type_id: row.service_type_id }
          : { validServices: [], service_type_id: null };

        if (!validServices.length || !service_type_id) return [];

        const shifts = getValidShifts(validServices, service_type_id);

        return shifts.map((shift: any) => ({ id: shift.id, name: shift.name }));
      }, true),

      // Auto populate shift from PO
      renderReadOnly: genericCellRender((_, row) => {
        if (mode === 'po') {
          const validServices = row._meta?.validServices || [];
          const { purchase_order_number } = row;
          if (!purchase_order_number) return '';
          const matchedService = validServices.find(
            (service: any) => service.purchase_order_number === purchase_order_number,
          );
          return matchedService?.shift?.name || 'No Shift';
        }
        return '';
      }),

      validation:
        mode === 'package'
          ? Yup.string()
              .nullable()
              .test('required-if-shifts', 'Shift is required when options are available', function (value) {
                const row = this.parent;
                const validServices = row?._meta?.validServices ?? [];
                const service_type_id = row?.service_type_id;
                if (!validServices.length || !service_type_id) return true;

                const shifts = getValidShifts(validServices, service_type_id);

                // If no shifts available, not required
                if (!shifts.length) return true;
                return !!value;
              })
          : Yup.mixed().nullable(),
    },
    {
      id: 'service_details',
      header: '',
      type: 'custom',
      style: { width: '50px' },
      classNames: {
        header: 'cell-align-center',
        column: 'cell-align-center',
      },
      component: ServiceDetail,
      componentProps: (row: Row) => ({
        row,
        wraps: row._meta?.assocWraps ?? [],
        items: row._meta?.assocItems ?? [],
      }),
      onChange: (row: Row) => {
        // Extract fields directly from service-details and return as row-level fields
        const details = row['service_details']?.details || {};
        return {
          sah_wrap_id: details.sah_wrap_id ?? null,
          sah_item_id: details.sah_item_id ?? null,
          sah_detail_description: details.sah_detail_description ?? null,
          sah_hpt_id: details.sah_hpt_id ?? null,
          sah_hpt_description: details.sah_hpt_description ?? null,
          is_first_payment_item: details.is_first_payment_item ?? null,
        };
      },
    },
    {
      id: 'account_code_id',
      header: 'Account Ref No.',
      type: 'select',
      style: { width: '150px' },
      options: accountInvoiceCodes,
    },
    {
      id: 'action_buttons',
      header: '',
      type: 'custom',
      style: { width: '36px' },
      component: RefreshActionButton,
      componentProps: {
        mode,
        clientId,
        setFormData,
      },
    },
  ];

  // Find by package
  const packageColumns: Column[] = [
    {
      id: 'client_package_id',
      header: 'Package Type',
      type: 'select',
      style: { width: '175px' },
      classNames: {
        cell: 'text-wrap',
      },
      options: packageOptions,
      renderEditor: selectEditor((row) => getPackageOptionsForDate(row.service_date), true),
      onChange: fetchValidServices,
      validation: Yup.string().required('Package Type is required'),
    },
    {
      id: 'service_type_id',
      header: 'Service Type',
      type: 'select',
      style: { width: '175px' },
      options: serviceTypes,
      renderEditor: selectEditor((row) => {
        const validServices = row._meta?.validServices;
        if (!validServices?.length) return [];

        const uniqueServiceTypeIds = [...new Set(validServices.map((valid: any) => valid.service_type_id))];

        const uniqueServiceTypes =
          uniqueServiceTypeIds
            .map((id) => serviceTypes.find((s) => s.id === id))
            .filter((service): service is ServiceTypeRow => Boolean(service))
            .map(({ id, name }) => ({ id, name })) ?? [];

        return uniqueServiceTypes;
      }, true),
      onChange: fetchSahWrapsAndItems,
      validation: Yup.number().required('Service Type is required'),
    },
  ];

  // Find by PO reference
  const poColumns: Column[] = [
    {
      id: 'purchase_order_number',
      header: 'Purchase Order #',
      type: 'select',
      style: { width: '350px' },
      renderEditor: selectEditor(
        (row) =>
          row._meta?.validServices?.map((valid: { purchase_order_number: string }) => ({
            id: valid.purchase_order_number,
            name: valid.purchase_order_number,
          })) ?? [],
        true,
      ),
      renderReadOnly: (value, _row) => {
        return value ?? '';
      },
      onChange: fetchSahWrapsAndItems,
      validation: Yup.string().required('Purchase Order # is required'),
    },
  ];

  // Build column list based on mode
  const modeColumns = mode === 'package' ? packageColumns : poColumns;

  return [...startColumns, ...modeColumns, ...endColumns];
};

/* Helper / Callback Functions */

// Fetch valid services for a given row + parent context (client_id, mode)
export const fetchValidServices = async (row: any, context: any) => {
  if (!context?.client_id) return {};
  const { client_id, mode } = context;
  const { service_date, client_package_id } = row;
  if (!client_id || !service_date) return {};

  const params: any = { client_id, date: service_date };
  if (mode === 'package' && client_package_id) params.package_id = client_package_id;

  const { payload } = await store.dispatch(getBudgetExternalServices(params));
  const validServices = payload?.data ?? [];

  const updates: any = { _meta: { validServices } };

  if (!deriveBudgetId({ ...row, ...updates })) {
    updates.client_budget_id = null;
  }

  // Clear service detail fields when package changes
  updates.sah_wrap_id = null;
  updates.sah_item_id = null;
  updates.sah_detail_description = null;
  updates.sah_hpt_id = null;
  updates.sah_hpt_description = null;
  updates.is_first_payment_item = null;

  return updates;
};

// Refresh all metadata for a single item row (budget services + wraps/items)
// Used by EditActionButtons and SupplierInvoiceModal to avoid duplication
export const refreshItemMeta = async (
  item: Row,
  clientId: number | undefined,
  mode: 'package' | 'po',
): Promise<{ client_budget_id: string | undefined; _meta: any }> => {
  const _meta: any = { ...(item as any)._meta };
  let client_budget_id: string | undefined = (item as any).client_budget_id ?? undefined;

  // Fetch budget services
  if (clientId && item.service_date) {
    const params: any = { client_id: clientId, date: item.service_date };
    if (mode === 'package' && item.client_package_id) {
      params.package_id = item.client_package_id;
    }

    const { payload } = await store.dispatch(getBudgetExternalServices(params));
    _meta.validServices = payload?.data ?? [];

    const match =
      _meta.validServices.find(
        (valid: any) =>
          (item.purchase_order_number && valid.purchase_order_number === item.purchase_order_number) ||
          (item.service_type_id && valid.service_type_id === item.service_type_id),
      ) ?? null;

    client_budget_id = match?.budget?.id ?? undefined;
  }

  // Fetch wraps/items
  if (item.service_type_id || item.purchase_order_number) {
    const changedKey = item.purchase_order_number ? 'purchase_order_number' : 'service_type_id';
    const assocUpdates = await fetchSahWrapsAndItems({ ...item, _meta }, undefined, changedKey);
    if (assocUpdates?._meta) {
      Object.assign(_meta, assocUpdates._meta);
    }
  }

  return { client_budget_id, _meta };
};

// Fetch sah_items and sah_wraps based on selected service_type_id or purchase_order_number
export const fetchSahWrapsAndItems = async (row: any, _context?: any, changedKey?: string) => {
  const validServices = row?._meta?.validServices ?? [];
  if (!validServices.length) return {};

  const { purchase_order_number, service_type_id } = row;

  const match =
    changedKey === 'purchase_order_number'
      ? validServices.find((valid: any) => valid.purchase_order_number === purchase_order_number)
      : validServices.find((valid: any) => valid.service_type_id === service_type_id);

  if (!match?.service_type_id) return {};

  try {
    const { data: assoc } = await store.dispatch(
      sahServiceTypeAssociationsApi.endpoints.getAssocByServiceType.initiate(
        { id: String(match.service_type_id) },
        { subscribe: false },
      ),
    );

    if (assoc) {
      const _meta = {
        ...(row._meta ?? {}),
        assocItems: assoc.items,
        assocWraps: assoc.wraps,
      };

      const wraps = assoc.wraps ?? [];
      const items = assoc.items ?? [];
      const updates: any = {
        _meta,
        client_budget_id: deriveBudgetId({ ...row, _meta }) ?? null,
      };

      // Keep shift_id in sync with PO/ST changes
      if (changedKey === 'purchase_order_number') {
        // Auto-populate from PO-selected service
        updates.shift_id = match?.shift?.id ?? null;
      } else if (changedKey === 'service_type_id') {
        // Clear stale shift if no longer valid for the selected service type
        const shifts = getValidShifts(validServices, match.service_type_id);
        if (!shifts.some((s: any) => String(s.id) === String(row.shift_id))) {
          updates.shift_id = null;
        }
      }

      // Clear all service detail fields when PO changes
      if (changedKey === 'purchase_order_number') {
        updates.sah_wrap_id = null;
        updates.sah_item_id = null;
        updates.sah_detail_description = null;
        updates.sah_hpt_id = null;
        updates.sah_hpt_description = null;
        updates.is_first_payment_item = null;
      } else {
        // Clear selected wrap/item if no longer valid
        if (!wraps.some((wrap: any) => wrap.id === row.sah_wrap_id)) {
          updates.sah_wrap_id = null;
        }
        if (!items.some((item: any) => item.id === row.sah_item_id)) {
          updates.sah_item_id = null;
        }
        // Reset first payment indicator as the service context may have changed
        updates.is_first_payment_item = null;
      }
      return updates;
    }
  } catch (err) {
    console.error('Error fetching service associations:', err);
    return {};
  }
};

function deriveBudgetId(row: any): string | null {
  const validServices = row?._meta?.validServices;
  if (!validServices?.length) return null;

  const match = row.purchase_order_number
    ? validServices.find((valid: any) => valid.purchase_order_number === row.purchase_order_number)
    : validServices.find((valid: any) => valid.service_type_id === row.service_type_id);

  return match?.budget?.id ?? null;
}

// Get unique shifts for the selected service type
function getValidShifts(services: any[], service_type_id: number): any[] {
  return services
    .filter((service: any) => service.service_type_id === service_type_id && service.shift)
    .map((service: any) => service.shift)
    .filter(
      (shift: any, index: number, current: any[]) =>
        index === current.findIndex((otherShift) => otherShift.id === shift.id),
    );
}

function deriveTotal({ units = 0, unit_amount = 0, gst_type = 'nin' }: any): number {
  let total = units * unit_amount;
  let multiplier = 1;
  switch (gst_type) {
    case 'nin': {
      // GST Exclusive - GST needs to be added
      multiplier = 1.1;
      total *= multiplier;
      break;
    }
    default: {
      break;
    }
  }

  return total;
}
